import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

/**
 * Fixed-proportion crop step for the tracking-page images, shown right after the merchant picks a
 * file — so whatever they upload, the stored image always matches the template placeholders:
 *   logo            → free crop (trim whitespace; templates scale logos by height, any shape works)
 *   banner desktop  → 6:1 strip (output capped at 1920px wide)
 *   banner mobile   → 2:1 (output capped at 750px wide)
 *
 * UX: dark work area, drag/resize handles, reset, and a
 * minimum-size gate (blocks blurry upscales from tiny sources) with a live px readout.
 * onConfirm receives a Blob of the cropped region (PNG stays PNG for transparency, else JPEG).
 * Kept identical in proship-web and proship-backend — change both together.
 */
export default function ImageCropDialog({ open, file, aspect, title, hint, outWidth = 1920, minWidth = 0, minHeight = 0, onCancel, onConfirm }) {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [src, setSrc] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Build/revoke the object URL per file.
  useEffect(() => {
    if (!file) { setSrc(''); return undefined; }
    const url = URL.createObjectURL(file);
    setSrc(url);
    setCrop(undefined);
    setCompletedCrop(null);
    setLoadError(false);
    setProcessing(false);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const initialCropFor = (width, height) => (aspect
    ? centerCrop(makeAspectCrop({ unit: '%', width: 100 }, aspect, width, height), width, height)
    : { unit: '%', x: 2, y: 2, width: 96, height: 96 });

  const applyInitialCrop = () => {
    const img = imgRef.current;
    if (!img) return;
    const initial = initialCropFor(img.width, img.height);
    setCrop(initial);
    // Make the initial selection usable without any drag: mirror it as the completed crop (in px).
    setCompletedCrop({
      unit: 'px',
      x: (initial.x / 100) * img.width,
      y: (initial.y / 100) * img.height,
      width: (initial.width / 100) * img.width,
      height: (initial.height / 100) * img.height,
    });
  };

  const confirm = async () => {
    const img = imgRef.current;
    const c = completedCrop;
    if (!img || !c || !c.width || !c.height || processing) { if (!processing) onCancel(); return; }
    setProcessing(true);
    try {
      // Displayed-px → natural-px
      const scaleX = img.naturalWidth / img.width;
      const scaleY = img.naturalHeight / img.height;
      const srcX = c.x * scaleX;
      const srcY = c.y * scaleY;
      const srcW = c.width * scaleX;
      const srcH = c.height * scaleY;
      // Never upscale beyond the source; cap at the slot's target width.
      const outW = Math.round(Math.min(outWidth, srcW));
      const outH = Math.round(outW * (srcH / srcW));
      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outW, outH);
      const mime = file && file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, mime, 0.9));
      if (blob) onConfirm(blob);
      else onCancel();
    } finally {
      setProcessing(false);
    }
  };

  // Natural-pixel size of the current selection — used for the minimum-size gate, so a tiny
  // source (e.g. a favicon picked as a logo) can't produce a blurry upscaled asset.
  const img = imgRef.current;
  const natW = img && completedCrop ? Math.round(completedCrop.width * (img.naturalWidth / img.width)) : 0;
  const natH = img && completedCrop ? Math.round(completedCrop.height * (img.naturalHeight / img.height)) : 0;
  const tooSmall = !!completedCrop && (natW < minWidth || natH < minHeight);

  const ratioLabel = aspect ? `${aspect}:1` : 'free';

  return (
    <Dialog open={open} onClose={processing ? undefined : onCancel} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{title}</Typography>
          <Chip size="small" variant="outlined" label={aspect ? `Locked ${ratioLabel}` : 'Free shape'} />
          {minWidth > 0 && <Chip size="small" variant="outlined" label={`Min ${minWidth}×${minHeight}px`} />}
        </Stack>
        {hint && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontWeight: 400 }}>
            {hint}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        {/* Dark work area: makes the image bounds and the dimmed-out region obvious */}
        <Box sx={{
          bgcolor: '#111827', borderRadius: '10px', p: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220,
        }}>
          {loadError ? (
            <Typography sx={{ color: '#F87171', fontSize: 14 }}>
              Could not read this image — please try a different file.
            </Typography>
          ) : src && (
            <ReactCrop
              crop={crop}
              onChange={(_pixelCrop, percentCrop) => setCrop(percentCrop)}
              onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
              aspect={aspect}
              keepSelection
              ruleOfThirds={!!aspect}
            >
              {/* eslint-disable-next-line jsx-a11y/alt-text */}
              <img ref={imgRef} src={src} onLoad={applyInitialCrop} onError={() => setLoadError(true)}
                style={{ maxWidth: '100%', maxHeight: '52vh', display: 'block' }} />
            </ReactCrop>
          )}
        </Box>

        {/* Feedback row: live result preview + size chips + reset */}
        {!loadError && completedCrop && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mt: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
              <Chip
                size="small"
                color={tooSmall ? 'error' : 'success'}
                variant={tooSmall ? 'filled' : 'outlined'}
                label={`Selection ${natW}×${natH}px`}
              />
              {tooSmall && (
                <Typography variant="caption" color="error">
                  Too small — need at least {minWidth}×{minHeight}px. Select a larger area or upload a bigger image.
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              <Button size="small" startIcon={<RestartAltIcon />} onClick={applyInitialCrop} sx={{ textTransform: 'none' }}>
                Reset
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={processing} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={confirm} disabled={tooSmall || loadError || !completedCrop || processing} sx={{ textTransform: 'none' }}>
          {processing ? 'Preparing…' : 'Crop & upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
